import { isNil } from 'lodash';
import { call, fork, put, takeEvery } from 'redux-saga/effects';
import RemoteImageApi from '../../../lib/api/remoteImage/RemoteImageApi';
import RemoteImageToastMsg from '../../../constants/service/toastMsg/remoteImageToastMsg';
import { remoteImageActions } from './index';
import {
  REMOTE_SPLASH_IMAGE_LIST_URL_PATH,
  REMOTE_REFERRAL_IMAGE_LIST_URL_PATH,
} from '../../../constants/service/common/urlConstants';
import { REMOTE_REFERRAL_IMAGE_TYPE } from '../../../constants/service/app/remoteImageConstants';
import { setRequestForm, reDirectPage, _setResponseListForm } from '../../../lib/common';
import toastMsg from '../../../lib/toastMsg';

function* fetchList() {
  /**
   *  원격 스플래시 이미지 리스트 요청하기
   */
  yield takeEvery(remoteImageActions.GET_REMOTE_IMAGE_SPLASH_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.getRemoteImageSplashList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const data = {
        remoteImageList: _setResponseListForm(result.data.list, result.data.totalCnt, payload),
      };

      yield put({ type: remoteImageActions.GET_REMOTE_IMAGE_SPLASH_LIST_SUCCESS, payload: data });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.GET_REMOTE_IMAGE_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 레퍼럴 이미지 리스트 요청하기
   */
  yield takeEvery(remoteImageActions.GET_REMOTE_IMAGE_REFERRAL_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.getRemoteImageReferralList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const data = {
        remoteImageList: _setResponseListForm(result.data.list, result.data.totalCnt, payload),
      };

      yield put({ type: remoteImageActions.GET_REMOTE_IMAGE_REFERRAL_LIST_SUCCESS, payload: data });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.GET_REMOTE_IMAGE_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 스플래시 이미지 상세 요청하기
   */
  yield takeEvery(remoteImageActions.GET_REMOTE_IMAGE_SPLASH_DETAIL_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.getRemoteImageSplashDetail, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const { memo, url, path, status } = result.data.remoteImageSplashDetail;

      const data = {
        remoteImageDetail: {
          remoteImg: [{ url, path }],
          memo,
          imgUrl: url,
          status,
          originStatus: status,
        },
      };

      yield put({ type: remoteImageActions.GET_REMOTE_IMAGE_SPLASH_DETAIL_SUCCESS, payload: data });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.GET_REMOTE_IMAGE_DETAIL_INFO_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 레퍼럴 이미지 상세 요청하기
   */
  yield takeEvery(remoteImageActions.GET_REMOTE_IMAGE_REFERRAL_DETAIL_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.getRemoteImageReferralDetail, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const { remoteImageReferralInfo, remoteImageReferralList } = result.data;

      const appImg = remoteImageReferralList.filter((item) => item.type === REMOTE_REFERRAL_IMAGE_TYPE.APP);
      const pcImg = remoteImageReferralList.filter((item) => item.type === REMOTE_REFERRAL_IMAGE_TYPE.PC);
      const tabletImg = remoteImageReferralList.filter((item) => item.type === REMOTE_REFERRAL_IMAGE_TYPE.TABLET);
      const mobileImg = remoteImageReferralList.filter((item) => item.type === REMOTE_REFERRAL_IMAGE_TYPE.MOBILE);

      const data = {
        remoteImageDetail: {
          ...remoteImageReferralInfo,
          originStatus: remoteImageReferralInfo.status,
          exposeLocationTypeEditable: isNil(remoteImageReferralInfo.exposeLocationType),
          appImg,
          pcImg,
          tabletImg,
          mobileImg,
          appImgUrl: appImg[0].url,
          pcImgUrl: pcImg[0].url,
          tabletImgUrl: tabletImg[0].url,
          mobileImgUrl: mobileImg[0].url,
        },
      };

      yield put({ type: remoteImageActions.GET_REMOTE_IMAGE_REFERRAL_DETAIL_SUCCESS, payload: data });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.GET_REMOTE_IMAGE_DETAIL_INFO_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 스플래시 이미지 등록 요청하기
   */
  yield takeEvery(remoteImageActions.CREATE_REMOTE_IMAGE_SPLASH_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.createRemoteImageSplash, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(RemoteImageToastMsg.CREATE_REMOTE_IMAGE_REQUEST_SUCCESS);

      /**
       *  리스트 페이지로 이동
       */
      reDirectPage({
        path: REMOTE_SPLASH_IMAGE_LIST_URL_PATH,
      });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.CREATE_REMOTE_IMAGE_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 레퍼럴 이미지 등록 요청하기
   */
  yield takeEvery(remoteImageActions.CREATE_REMOTE_IMAGE_REFERRAL_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.createRemoteImageReferral, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(RemoteImageToastMsg.CREATE_REMOTE_IMAGE_REQUEST_SUCCESS);

      /**
       *  리스트 페이지로 이동
       */
      reDirectPage({
        path: REMOTE_REFERRAL_IMAGE_LIST_URL_PATH,
      });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.CREATE_REMOTE_IMAGE_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 스플래시 이미지 수정 요청하기
   */
  yield takeEvery(remoteImageActions.EDIT_REMOTE_IMAGE_SPLASH_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.editRemoteImageSplash, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(RemoteImageToastMsg.EDIT_REMOTE_IMAGE_REQUEST_SUCCESS);

      /**
       *  리스트 페이지로 이동
       */
      reDirectPage({
        path: REMOTE_SPLASH_IMAGE_LIST_URL_PATH,
      });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.EDIT_REMOTE_IMAGE_REQUEST_ERROR, error);
    }
  });

  /**
   *  원격 레퍼럴 이미지 수정 요청하기
   */
  yield takeEvery(remoteImageActions.EDIT_REMOTE_IMAGE_REFERRAL_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(RemoteImageApi.editRemoteImageReferral, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      toastMsg.success(RemoteImageToastMsg.EDIT_REMOTE_IMAGE_REQUEST_SUCCESS);

      /**
       *  리스트 페이지로 이동
       */
      reDirectPage({
        path: REMOTE_REFERRAL_IMAGE_LIST_URL_PATH,
      });
    } catch (error) {
      toastMsg.error(RemoteImageToastMsg.EDIT_REMOTE_IMAGE_REQUEST_ERROR, error);
    }
  });
}

export default function* remoteImageSaga() {
  yield fork(fetchList);
}
